import { ALL_LNGS_NAMESPACES } from '@ingenico/util-i18n-config';
import { Banner } from '@ingenico/react-ui/banner';
import { ModalConfirmationDialog } from '@ingenico/react-ui/confirmation-dialog';
import { CoreTypography, CoreTypographyVariant } from '@ingenico/react-ui/core-typography';
import type { ReactNode } from 'react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { DashboardRowDataType } from '../../../domain/model/merchant.model';
import { DashboardRow } from '../dashboard-row/DashboardRow.ctrl';
import { useDeviceManagementCapability } from '../../hooks';
import { DashboardSectionSkeleton } from '../dashboard-section-skeleton/DashboardSectionSkeleton';

type SyncConfigurationScope = 'merchant' | 'store';

export interface SyncConfigurationEntityModalProps {
  readonly scope: SyncConfigurationScope;
  readonly isOpen: boolean;
  readonly isLoadingSubmission?: boolean;
  readonly isLoading?: boolean;
  readonly onSync: () => void;
  readonly onCancel?: () => void;
  readonly devicesClassName: string;
  readonly dashboardRowData: DashboardRowDataType;
}

export function SyncConfigurationModal({
  scope,
  isOpen,
  isLoadingSubmission,
  onSync,
  onCancel,
  devicesClassName,
  dashboardRowData,
  isLoading,
}: SyncConfigurationEntityModalProps): ReactNode {
  const namespace = scope === 'merchant' ? ALL_LNGS_NAMESPACES.MERCHANT : ALL_LNGS_NAMESPACES.STORE;
  const modalKey = scope === 'merchant' ? 'syncConfigurationMerchantModal' : 'syncConfigurationStoreModal';
  const devicesLabelKey = scope === 'merchant' ? 'merchantDevices' : 'storeDevices';
  const enableConfigProfileOrdering = useDeviceManagementCapability('logicalDevice.configProfile.enableOrdering');
  const titleKey = enableConfigProfileOrdering ? 'banner.titleOnConfigProfileOrdering' : 'banner.title_ff_off';
  const confirmButtonDisabled = useMemo(
    () => dashboardRowData?.isError || dashboardRowData?.numberOfEntities === 0 || !dashboardRowData?.numberOfEntities,
    [dashboardRowData],
  );
  const { t } = useTranslation([namespace, ALL_LNGS_NAMESPACES.COMMON]);

  return (
    <ModalConfirmationDialog
      cancelLabel={t('button.cancel', { ns: ALL_LNGS_NAMESPACES.COMMON })}
      confirmLabel={t(`${modalKey}.syncConfigurationButton`, { ns: namespace })}
      i18nNamespaces={[namespace, ALL_LNGS_NAMESPACES.COMMON]}
      isConfirmLoading={isLoadingSubmission}
      isOpen={isOpen}
      content={
        <>
          <Banner
            message={enableConfigProfileOrdering ? '' : t(`${modalKey}.banner.message`, { ns: namespace })}
            title={t(`${modalKey}.${titleKey}`, { ns: namespace })}
            severity={'Warning'}
          />
          <CoreTypography variant={CoreTypographyVariant.Body02Medium} className={devicesClassName}>
            {t(`${modalKey}.${devicesLabelKey}`, { ns: namespace })}
          </CoreTypography>
          {isLoading ? <DashboardSectionSkeleton /> : <DashboardRow dashboardRowData={dashboardRowData} />}
        </>
      }
      onClose={onCancel}
      onConfirm={onSync}
      title={t(`${modalKey}.title`, { ns: namespace })}
      confirmButtonDisabled={confirmButtonDisabled}
    />
  );
}
